import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api, formatCzk, formatDate, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { StatusBadge } from "@/components/StatusBadge";
import { Inbox, Package, Wallet, Star, ShieldCheck, ShieldAlert, MapPin, Bell, BellOff, BellRing } from "lucide-react";
import { toast } from "sonner";
import { enablePush, disablePush, sendTestPush, getPushState, pushSupported } from "@/lib/push";

const ProviderDashboard = () => {
  const { user } = useAuth();
  const [available, setAvailable] = useState([]);
  const [mine, setMine] = useState([]);
  const [profile, setProfile] = useState(null);
  const [verification, setVerification] = useState({ status: "not_submitted" });
  const [reviews, setReviews] = useState([]);
  const [push, setPush] = useState({ supported: false, subscribed: false, permission: "default" });
  const [pushBusy, setPushBusy] = useState(false);

  const load = async () => {
    try {
      const [a, m, p, v] = await Promise.all([
        api.get("/orders/available"),
        api.get("/orders/mine"),
        api.get("/providers/me/profile"),
        api.get("/providers/me/verification").catch(() => ({ data: { status: "not_submitted" } })),
      ]);
      setAvailable(a.data);
      setMine(m.data);
      setProfile(p.data);
      setVerification(v.data);
      if (user?.id) {
        try {
          const { data: r } = await api.get(`/reviews/provider/${user.id}`);
          setReviews(r);
        } catch (err) { console.error("Failed to load reviews:", err); }
      }
    } catch (e) {
      console.error("Failed to load provider data:", e);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    getPushState().then(setPush).catch(() => {});
  }, []);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (push.subscribed) {
        await disablePush(api);
        toast.success("Notifikace vypnuty.");
      } else {
        await enablePush(api);
        toast.success("Notifikace zapnuty! Jakmile přijde zakázka ve vašem městě, dostanete upozornění.");
      }
      setPush(await getPushState());
    } catch (e) {
      toast.error(e?.message || String(e));
    } finally {
      setPushBusy(false);
    }
  };

  const testPush = async () => {
    setPushBusy(true);
    try {
      const res = await sendTestPush(api);
      if (res?.sent > 0) toast.success(`Testovací notifikace odeslána (${res.sent}×).`);
      else toast.warning("Žádná aktivní subscripce. Zkus nejdřív zapnout notifikace.");
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setPushBusy(false);
    }
  };

  const accept = async (orderId) => {
    try {
      const { data } = await api.post(`/orders/${orderId}/accept`);
      if (data?.status === "awaiting_payment") {
        toast.success("Zakázka přijata! Zákazník obdržel e-mail s výzvou k platbě.");
      } else {
        toast.success("Objednávka přijata!");
      }
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const stats = {
    active: mine.filter((o) => !["completed", "cancelled"].includes(o.status)).length,
    completed: mine.filter((o) => o.status === "completed").length,
    earned: mine.filter((o) => o.status === "completed").reduce((s, o) => s + (o.provider_payout || 0), 0),
    rating: profile ? (profile.rating_count ? (profile.rating_sum / profile.rating_count).toFixed(1) : "—") : "—",
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container-page py-10 space-y-6">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-sky-100 text-sky-700 grid place-items-center"><Inbox className="w-5 h-5" /></div>
              <div>
                <div className="text-xs text-slate-500">Aktivní</div>
                <div className="font-display text-2xl font-bold" data-testid="prov-stat-active">{stats.active}</div>
              </div>
            </div>
          </Card>
          <Card className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 grid place-items-center"><Package className="w-5 h-5" /></div>
              <div>
                <div className="text-xs text-slate-500">Dokončené</div>
                <div className="font-display text-2xl font-bold" data-testid="prov-stat-completed">{stats.completed}</div>
              </div>
            </div>
          </Card>
          <Card className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-700 grid place-items-center"><Wallet className="w-5 h-5" /></div>
              <div>
                <div className="text-xs text-slate-500">Vyděláno celkem</div>
                <div className="font-display text-2xl font-bold" data-testid="prov-stat-earned">{formatCzk(stats.earned)}</div>
              </div>
            </div>
          </Card>
          <Card className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-700 grid place-items-center"><Star className="w-5 h-5" /></div>
              <div>
                <div className="text-xs text-slate-500">Hodnocení</div>
                <div className="font-display text-2xl font-bold" data-testid="prov-stat-rating">
                  {stats.rating}{profile?.rating_count > 0 && <span className="text-sm text-slate-400"> /5</span>}
                </div>
              </div>
            </div>
          </Card>
        </div>

        {verification.status !== "approved" && (
          <Card className={`p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${
            verification.status === "rejected" ? "bg-rose-50 border-rose-200" :
            verification.status === "pending" ? "bg-amber-50 border-amber-200" :
            "bg-sky-50 border-sky-200"
          }`}>
            <div className="flex items-center gap-3">
              {verification.status === "pending" ? <ShieldAlert className="w-6 h-6 text-amber-600" /> : <ShieldCheck className="w-6 h-6 text-sky-600" />}
              <div>
                <div className="font-display font-bold text-slate-900">
                  {verification.status === "pending" ? "Vaše verifikace se ověřuje" :
                   verification.status === "rejected" ? "Verifikace zamítnuta – doplňte podklady" :
                   "Ověřte se a získejte více zakázek"}
                </div>
                <div className="text-sm text-slate-600">
                  {verification.status === "pending" ? "Obvykle do 24 hodin." :
                   verification.status === "rejected" ? (verification.rejection_reason || "Nahrajte znovu doklady.") :
                   "Nahrajte OP, selfie a číslo účtu pro vyplácení odměn."}
                </div>
              </div>
            </div>
            {verification.status !== "pending" && (
              <Link to="/dashboard/provider/verifikace" className="cta-primary text-sm py-2 px-5" data-testid="prov-verify-cta">
                {verification.status === "rejected" ? "Doplnit dokumenty" : "Spustit ověření"}
              </Link>
            )}
          </Card>
        )}

        {push.supported && (
          <Card
            className={`p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${
              push.subscribed ? "bg-emerald-50 border-emerald-200" : "bg-sky-50 border-sky-200"
            }`}
            data-testid="prov-push-card"
          >
            <div className="flex items-start gap-3">
              {push.subscribed ? (
                <BellRing className="w-6 h-6 text-emerald-600 flex-shrink-0" />
              ) : (
                <Bell className="w-6 h-6 text-sky-600 flex-shrink-0" />
              )}
              <div>
                <div className="font-display font-bold text-slate-900">
                  {push.subscribed
                    ? "Notifikace na telefonu zapnuté"
                    : "Zapněte si notifikace na telefon"}
                </div>
                <div className="text-sm text-slate-600">
                  {push.subscribed
                    ? "Jakmile přijde nová zakázka ve vašem městě, dostanete upozornění přímo do prohlížeče / na plochu telefonu — i když web nemáte otevřený."
                    : "Dostávejte okamžité upozornění, když se objeví nová zakázka ve vašem městě. Funguje v Chromu na Androidu a jako PWA na iPhone (iOS 16.4+)."}
                </div>
                {push.permission === "denied" && (
                  <div className="text-xs text-rose-700 mt-1">
                    Oprávnění je zakázané v nastavení prohlížeče. Povolte notifikace a obnovte stránku.
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              {push.subscribed && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={testPush}
                  disabled={pushBusy}
                  data-testid="prov-push-test-btn"
                >
                  Test
                </Button>
              )}
              <Button
                type="button"
                size="sm"
                onClick={togglePush}
                disabled={pushBusy || push.permission === "denied"}
                className={push.subscribed ? "" : "cta-primary"}
                variant={push.subscribed ? "outline" : "default"}
                data-testid="prov-push-toggle-btn"
              >
                {push.subscribed ? (
                  <>
                    <BellOff className="w-4 h-4 mr-1.5" /> Vypnout
                  </>
                ) : (
                  <>
                    <Bell className="w-4 h-4 mr-1.5" /> Zapnout notifikace
                  </>
                )}
              </Button>
            </div>
          </Card>
        )}


        <Tabs defaultValue="available">
          <TabsList className="grid w-full sm:w-auto sm:inline-grid grid-cols-2 sm:grid-cols-4">
            <TabsTrigger value="available" data-testid="prov-tab-available">
              Dostupné ({available.length})
            </TabsTrigger>
            <TabsTrigger value="mine" data-testid="prov-tab-mine">Mé zakázky ({mine.length})</TabsTrigger>
            <TabsTrigger value="reviews" data-testid="prov-tab-reviews">Hodnocení ({reviews.length})</TabsTrigger>
            <TabsTrigger value="profile" data-testid="prov-tab-profile">Profil prádelny</TabsTrigger>
          </TabsList>

          <TabsContent value="available" className="mt-6">
            {available.length === 0 ? (
              <Card className="p-10 text-center text-slate-500">
                Žádné nové objednávky ve vašem městě <span className="text-slate-700 font-semibold">({profile?.city || "není nastaveno"})</span>.
              </Card>
            ) : (
              <div className="grid gap-3">
                {available.map((o) => (
                  <Link key={o.id} to={`/order/${o.id}`} data-testid={`prov-available-${o.id}`} className="block">
                    <Card className="p-5 flex items-center justify-between gap-4 hover:border-sky-300 hover:shadow-sm transition-all cursor-pointer">
                      <div>
                        <div className="font-semibold text-slate-900">
                          {o.kg} kg · {o.service_type === "premium" ? "Premium" : "Standard"}
                          {o.express_type !== "none" && <span className="ml-1 text-xs text-amber-600">{o.express_type === "24h" ? "Expres 24h" : "Same-day"}</span>}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                          <span>{o.pickup_city} {o.pickup_zip && `· ${o.pickup_zip}`}</span>
                          {o.distance_km != null && (
                            <span className="inline-flex items-center gap-1 text-sky-700 bg-sky-100 px-2 py-0.5 rounded-full font-semibold">
                              <MapPin className="w-3 h-3" /> {o.distance_km} km
                            </span>
                          )}
                          <span>· vyzvednutí {formatDate(o.pickup_time)}</span>
                        </div>
                        {o.notes && <div className="mt-1 text-xs text-slate-500 italic">&ldquo;{o.notes}&rdquo;</div>}
                        <div className="mt-1 text-[11px] text-sky-700 font-semibold">Klikněte pro detail →</div>
                        <div className="mt-1 text-[11px] text-slate-500">Po přijetí zákazník obdrží výzvu k platbě.</div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="font-display text-xl font-extrabold text-slate-900">{formatCzk(o.provider_payout)}</div>
                          <div className="text-[10px] text-slate-500 uppercase tracking-wider">Váš podíl</div>
                        </div>
                        <Button
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); accept(o.id); }}
                          data-testid={`prov-accept-${o.id}`}
                        >
                          Přijmout
                        </Button>
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="mine" className="mt-6">
            {mine.length === 0 ? (
              <Card className="p-10 text-center text-slate-500">Zatím žádné přijaté zakázky.</Card>
            ) : (
              <div className="grid gap-3">
                {mine.map((o) => (
                  <Link key={o.id} to={`/order/${o.id}`} data-testid={`prov-order-${o.id}`}>
                    <Card className="p-5 flex items-center justify-between gap-4 hover:border-sky-300 transition-all">
                      <div>
                        <div className="font-semibold text-slate-900">
                          {o.kg} kg · {o.service_type === "premium" ? "Premium" : "Standard"}
                        </div>
                        <div className="text-xs text-slate-500">
                          {o.customer_name} · {o.pickup_address} · {o.pickup_city}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <StatusBadge status={o.status} />
                        <div className="font-display font-bold">{formatCzk(o.provider_payout)}</div>
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="profile" className="mt-6">
            <ProviderProfileForm profile={profile} onSaved={load} />
          </TabsContent>

          <TabsContent value="reviews" className="mt-6">
            {reviews.length === 0 ? (
              <Card className="p-10 text-center text-slate-500" data-testid="prov-reviews-empty">
                Zatím žádná hodnocení. Po dokončení objednávek vám zákazníci mohou ohodnotit službu.
              </Card>
            ) : (
              <div className="grid gap-4">
                <Card className="p-5 flex items-center gap-4 bg-amber-50 border-amber-200">
                  <Star className="w-7 h-7 text-amber-500 fill-amber-500" />
                  <div>
                    <div className="text-xs text-amber-800 uppercase tracking-wider font-semibold">Průměr</div>
                    <div className="font-display text-3xl font-extrabold text-slate-900" data-testid="prov-reviews-avg">
                      {(reviews.reduce((s, r) => s + (r.overall || 0), 0) / reviews.length).toFixed(1)} <span className="text-base text-slate-500">/5 z {reviews.length} hodnocení</span>
                    </div>
                  </div>
                </Card>
                {reviews.map((r) => (
                  <Card key={r.id} className="p-5" data-testid={`prov-review-${r.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold text-slate-900">{r.from_user_name}</div>
                      <div className="flex gap-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star key={i} className={`w-4 h-4 ${i < (r.overall || 0) ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} />
                        ))}
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 mb-2">{formatDate(r.created_at)}</div>
                    <div className="grid grid-cols-3 gap-2 text-xs text-slate-600 mb-2">
                      <span>Čistota: <strong className="text-slate-900">{r.cleanliness}/5</strong></span>
                      <span>Komunikace: <strong className="text-slate-900">{r.communication}/5</strong></span>
                      <span>Rychlost: <strong className="text-slate-900">{r.speed}/5</strong></span>
                    </div>
                    {r.comment && <p className="text-sm text-slate-700 italic">&ldquo;{r.comment}&rdquo;</p>}
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
};

const ProviderProfileForm = ({ profile, onSaved }) => {
  const [form, setForm] = useState({
    business_name: "",
    description: "",
    city: "",
    zip: "",
    services: ["standard", "premium"],
    capacity_per_week_kg: 50,
    avatar_url: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (profile) {
      setForm({
        business_name: profile.business_name || "",
        description: profile.description || "",
        city: profile.city || "",
        zip: profile.zip || "",
        services: profile.services || ["standard", "premium"],
        capacity_per_week_kg: profile.capacity_per_week_kg || 50,
        avatar_url: profile.avatar_url || "",
      });
    }
  }, [profile]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/providers/me", form);
      toast.success("Profil uložen");
      onSaved?.();
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setSaving(false); }
  };

  const toggleService = (s) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s],
    }));

  return (
    <Card className="p-7">
      <h2 className="font-display text-2xl font-bold text-slate-900">Profil prádelny</h2>
      <p className="text-sm text-slate-500 mt-1">Tyto informace uvidí zákazníci v okolí.</p>
      <form onSubmit={save} className="mt-6 grid md:grid-cols-2 gap-5">
        <div className="md:col-span-2">
          <Label>Název prádelny</Label>
          <Input required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="profile-business-name" />
        </div>
        <div className="md:col-span-2">
          <Label>Popis</Label>
          <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="O vás, vašich zkušenostech, vybavení…" data-testid="profile-description" />
        </div>
        <div>
          <Label>Město působnosti</Label>
          <Input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="profile-city" />
        </div>
        <div>
          <Label>PSČ</Label>
          <Input value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} data-testid="profile-zip" />
        </div>
        <div>
          <Label>Kapacita / týden (kg)</Label>
          <Input type="number" min="10" value={form.capacity_per_week_kg} onChange={(e) => setForm({ ...form, capacity_per_week_kg: parseInt(e.target.value) || 0 })} data-testid="profile-capacity" />
        </div>
        <div>
          <Label>Nabízené služby</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {["standard", "premium"].map((s) => (
              <button key={s} type="button" onClick={() => toggleService(s)} data-testid={`profile-service-${s}`}
                className={`px-3 py-1.5 rounded-full text-sm border ${form.services.includes(s) ? "bg-sky-600 text-white border-sky-600" : "bg-white border-slate-200"}`}>
                {s === "premium" ? "Premium (žehlení)" : "Standard (složení)"}
              </button>
            ))}
          </div>
        </div>
        <div className="md:col-span-2">
          <Button type="submit" disabled={saving} data-testid="profile-save">{saving ? "Ukládám…" : "Uložit profil"}</Button>
        </div>
      </form>
    </Card>
  );
};

export default ProviderDashboard;
